import {
  AutoRedemptionSettingStrategy,
  AutoRedemptionSettingType,
} from '@rd/types/auto-redemption-setting.type';
import { z } from 'zod';

export const periodAutoRedemptionSettingStrategyOptionsSchema = z.object({
  month_frequency: z.union([
    z.literal(1),
    z.literal(3),
    z.literal(6),
    z.literal(12),
  ]),
  start_date: z.string(),
});

export const baseAutoRedemptionSettingModelAttributesSchema = z.object({
  id: z.string(),
  userId: z.string(),
  pointsAccountId: z.string(),
  pointsRequired: z.number(),
  strategy: z.nativeEnum(AutoRedemptionSettingStrategy),
  strategyOptions: z.union([
    periodAutoRedemptionSettingStrategyOptionsSchema,
    z.object({}),
  ]),
  redeemAt: z.string().nullable(),
  description: z.string(),
  productSku: z.string(),
});

export const digitalGiftCardAutoRedemptionSettingModelAttributesSchema =
  baseAutoRedemptionSettingModelAttributesSchema.extend({
    data: z.object({
      minimumTransfer: z.number(),
      productName: z.string(),
      productImageUrl: z.string(),
      thresholdAmount: z.number().nullable(),
      giftCardId: z.string(),
      recipient: z.object({
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
        phone: z.string(),
        message: z.string(),
      }),
      displayValue: z.number(),
    }),
  });

export const digitalGiftCardAutoRedemptionSettingModelSchema = z.object({
  id: z.string(),
  type: z.literal(AutoRedemptionSettingType.DigitalGiftCard),
  attributes: digitalGiftCardAutoRedemptionSettingModelAttributesSchema,
});

export const physicalGiftCardAutoRedemptionSettingModelAttributesSchema =
  baseAutoRedemptionSettingModelAttributesSchema.extend({
    data: z.object({
      productName: z.string(),
      productImageUrl: z.string(),
      thresholdAmount: z.number().nullable(),
      minimumTransfer: z.number(),
      giftCardId: z.string(),
      recipient: z.object({
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
        message: z.string(),
        companyName: z.string(),
        phoneNumber: z.string(),
        address: z.object({
          line1: z.string(),
          line2: z.string(),
          city: z.string(),
          countryCode: z.string(),
          postalCode: z.string(),
          state: z.string(),
        }),
      }),
      displayValue: z.number(),
    }),
  });

export const physicalGiftCardAutoRedemptionSettingModelSchema = z.object({
  id: z.string(),
  type: z.literal(AutoRedemptionSettingType.PhysicalGiftCard),
  attributes: physicalGiftCardAutoRedemptionSettingModelAttributesSchema,
});

export const cashAutoRedemptionSettingModelAttributesSchema =
  baseAutoRedemptionSettingModelAttributesSchema.extend({
    data: z.object({
      details: z.union([
        z.object({
          groupName: z.string(),
          productCode: z.string(),
        }),
        z.object({
          bsbNumber: z.string(),
        }),
      ]),
      productName: z.string(),
      productImageUrl: z.string(),
      minimumTransfer: z.number(),
      thresholdAmount: z.number().nullable(),
      membershipId: z.string(),
      number: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      enrollmentId: z.union([z.string(), z.never()]),
    }),
  });

export const cashRedemptionAutoRedemptionSettingModelSchema = z.object({
  id: z.string(),
  type: z.literal(AutoRedemptionSettingType.Cash),
  attributes: cashAutoRedemptionSettingModelAttributesSchema,
});

export const pointsTransferAutoRedemptionSettingModelAttributesSchema =
  baseAutoRedemptionSettingModelAttributesSchema.extend({
    data: z.object({
      firstName: z.string(),
      lastName: z.string(),
      productName: z.string(),
      productImageUrl: z.string(),
      thresholdAmount: z.number().nullable(),
      minimumTransfer: z.number(),
      membershipId: z.string(),
      number: z.string(),
    }),
  });

export const pointsTransferAutoRedemptionSettingModelSchema = z.object({
  id: z.string(),
  type: z.literal(AutoRedemptionSettingType.PointsTransfer),
  attributes: pointsTransferAutoRedemptionSettingModelAttributesSchema,
});

export const autoRedemptionSettingModelSchema = z.discriminatedUnion('type', [
  digitalGiftCardAutoRedemptionSettingModelSchema,
  physicalGiftCardAutoRedemptionSettingModelSchema,
  cashRedemptionAutoRedemptionSettingModelSchema,
  pointsTransferAutoRedemptionSettingModelSchema,
]);

export const autoRedemptionSettingPostResponseSchema = z.object({
  data: autoRedemptionSettingModelSchema,
});

export const autoRedemptionSettingPutResponseSchema = z.object({
  data: autoRedemptionSettingModelSchema,
});

export const autoRedemptionSettingIndexResponseSchema = z.object({
  data: z.array(autoRedemptionSettingModelSchema),
});
